// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Geometria da grelha e cálculo da janela visível
// ============================================================================
//
// PORQUE A ALTURA DA LINHA É 52 px E NÃO OUTRA
//   O *spike* que resolveu o D-005 mediu com 14 px por sublinha e 3 sublinhas
//   (42 px). Duas dessas bandas passaram a ser **barras contínuas**, o que reduz
//   muito o número de nós por linha — desenham-se ~5 barras em vez de 40 textos
//   repetidos — e por isso houve margem para dar altura em vez de a poupar.
//
//   ⚠️ Mudar estas medidas muda o número de dias visíveis e portanto invalida a
//   medição. Quem as alterar tem de voltar a correr `medicao.tsx`.
//
// PORQUE A JANELA É CALCULADA AQUI E NÃO NO COMPONENTE
//   É a única aritmética do mapa que erra em silêncio: um índice trocado mostra o
//   dia errado e a grelha continua com bom aspecto. Isolada, testa-se.
// ============================================================================

// AS TRÊS BANDAS — e porque as medidas somam exactamente a altura da linha
//
// ⚠️ O cliente apontou que «os motoristas sobrepõem as restantes linhas». As
// bandas estavam encostadas umas às outras sem folga nem separador, e a barra do
// motorista de uma linha ficava colada à barra de itinerário da linha seguinte —
// o olho agrupava-as como se fossem a mesma viatura.
//
// Corrigido com três coisas: mais altura por banda, uma **calha** de 2 px entre
// bandas, e um separador de linha visível. As medidas abaixo somam com as calhas,
// e o teste `dimensoes.test.ts` guarda essa soma para não voltar a sobrepor-se.
export const ALTURA_BARRA_ITINERARIO = 18;
export const ALTURA_LOCALIDADE = 14;
export const ALTURA_BARRA_MOTORISTA = 16;
/** Folga entre bandas. Sem ela as bandas tocam-se e leem-se como um bloco. */
export const CALHA = 1;
/** Separador entre linhas de viatura. É o que faz cada linha ler-se como uma. */
export const SEPARADOR_LINHA = 2;

export const ALTURA_LINHA =
  ALTURA_BARRA_ITINERARIO +
  CALHA +
  ALTURA_LOCALIDADE +
  CALHA +
  ALTURA_BARRA_MOTORISTA +
  SEPARADOR_LINHA;

/**
 * As três bandas para uma altura de linha qualquer.
 *
 * Existe porque o utilizador passou a poder **esticar a linha**, como no Excel:
 * *«algumas estão muito apertadas e não dá para ver tudo»*. As bandas têm de
 * crescer com ela, senão esticar a linha só acrescentava espaço vazio.
 *
 * ⚠️ **As calhas e o separador NÃO crescem.** São 1 px e 2 px porque é o que
 * separa visualmente, não porque sejam proporcionais a nada — e foi a falta deles
 * que produziu a queixa «os motoristas sobrepõem as restantes linhas». Cresce o
 * que serve para ler; não cresce o que serve para separar.
 *
 * ⚠️ **A soma tem de dar exactamente `alturaLinha`.** Se der menos, sobra uma
 * fenda; se der mais, a banda do motorista entra na linha seguinte — que é
 * precisamente o defeito que as calhas corrigiram. Por isso a última banda é o
 * **resto** e não um arredondamento próprio, e há teste sobre toda a gama.
 */
export interface Bandas {
  readonly itinerario: number;
  readonly localidade: number;
  readonly motorista: number;
}

export function bandas(alturaLinha: number): Bandas {
  const fixo = CALHA + CALHA + SEPARADOR_LINHA;
  const util = Math.max(3, alturaLinha - fixo);
  const base = ALTURA_BARRA_ITINERARIO + ALTURA_LOCALIDADE + ALTURA_BARRA_MOTORISTA;
  const itinerario = Math.round((util * ALTURA_BARRA_ITINERARIO) / base);
  const localidade = Math.round((util * ALTURA_LOCALIDADE) / base);
  return { itinerario, localidade, motorista: util - itinerario - localidade };
}

/** Largura de omissão de uma coluna de dia. O utilizador pode arrastá-la. */
export const LARGURA_DIA = 40;

// ⚠️ OS MÍNIMOS SÃO UMA DECISÃO DE DESEMPENHO, NÃO DE GOSTO.
//
// Apertar as medidas põe mais células no ecrã, e é o número de células desenhadas
// que governa o p95 do ADR-001. Num ecrã de 1600 × 900, com a coluna das viaturas
// em 176 px e a margem de 3:
//
//   omissão  40 × 52  →  24 linhas × 43 dias = **1 032** células desenhadas
//   mínimo   32 × 46  →  26 linhas × 52 dias = **1 352** células — 1,31× mais
//
// ⚠️ A primeira escolha foi 28 × 44, e estava errada: dava **1 566** e o teste
// `dimensoes.test.ts` — que existe precisamente para vigiar o risco R1 do ADR-001 —
// recusa acima de 1 400. Os mínimos foram apertados até caberem no orçamento que já
// estava escrito, em vez de se subir o tecto para eles caberem.
//
// O mínimo existe para permitir varrer o ano, não para chegar ao limite do que o
// navegador aguenta. Esticar é de graça: quanto maior a medida, menos células cabem
// e menos trabalho há — logo os máximos não precisam de justificação de desempenho.
export const LARGURA_DIA_MIN = 32;
export const LARGURA_DIA_MAX = 140;
export const ALTURA_LINHA_MIN = 46;
export const ALTURA_LINHA_MAX = 120;

/**
 * Espessura da tira sensível na borda, em píxeis.
 *
 * ⚠️ **Tem de coincidir com `mapa.css`**, onde as tiras são pseudo-elementos de
 * 6 px (`.dia-cab::after`, `.viatura::after`). O CSS não pode importar daqui, logo
 * a coincidência é garantida por teste em `mapa.css.test.ts` — se divergirem, o
 * cursor muda num sítio e o arrasto começa noutro, que é o pior dos dois mundos.
 */
export const LARGURA_TIRA = 6;

/** Largura de omissão da coluna fixa da esquerda. O utilizador pode arrastá-la. */
export const LARGURA_VIATURA = 176;
export const LARGURA_VIATURA_MIN = 96;
export const LARGURA_VIATURA_MAX = 340;

/** Altura do cabeçalho fixo: faixa do mês + faixa do dia. */
export const ALTURA_CABECALHO = 38;

/**
 * A secção dos subcontratados: título e linha. **Serve também a secção EM ESPERA**, que usa a
 * mesma máquina.
 *
 * ⚠️⚠️ **DUAS bandas desde 2026-09-16, e por isso 36 px e não 30.**
 *
 * 📗 Cliente: *«Subcontratados com UI muito parecido no mapa aos pedidos convencionais, com as
 * cidades por dias seguindo a mesma estrutura»* — a banda 2 é a cidade de cada dia
 * (`BandaDeLocais` em `Mapa.tsx`), a mesma que a frota mostra em `.dia-local`.
 *
 * ⚠️ **Continua mais baixa do que uma linha de viatura (52 px), e agora por UMA razão só:**
 * não há banda de motorista. 📗 *«a única diferença é que não se indica o motorista»* — é
 * literalmente a diferença que o cliente nomeou, e é a única que fica. 📌 **Não se copiaram os
 * 52 px por simetria:** a banda 3 da frota mede 16 px + 1 de calha, e reservá-la aqui deixava
 * 17 px vazios em todas as linhas a dizer que falta ali qualquer coisa.
 *
 * A conta, contra a da frota (18 + 1 + 14 + 1 + 16 + 2 = 52):
 *
 * | | frota | parceiros e espera |
 * |---|---|---|
 * | folga de topo | 0 | 3 |
 * | banda 1 · serviço | 18 | 19 |
 * | calha | 1 | 1 |
 * | banda 2 · cidade do dia | 14 | 11 |
 * | banda 3 · motorista | 16 + 1 | **não existe** |
 * | separador | 2 | 2 |
 * | **total** | **52** | **36** |
 *
 * ⚠️ **O custo é a ALTURA da secção, e é o mesmo tipo de custo que o C₂ declarou (+533 px):**
 * com as 39 linhas que o C₂ mediu, 1 170 → **1 404 px**, mais **234**. Fica declarado — não se
 * compra com um tecto, porque um tecto é o que o B28 tinha e era a parte que falhava.
 */
export const ALTURA_TITULO_SUB = 24;
export const ALTURA_LINHA_SUB = 36;

/** Altura de omissão do painel de detalhe **aberto**. Fechado, é só a barra. */
export const ALTURA_PAINEL = 200;
export const ALTURA_PAINEL_MIN = 120;
export const ALTURA_PAINEL_MAX = 520;

/**
 * Linhas e colunas desenhadas para além do que se vê.
 *
 * Sem margem, o primeiro pixel de deslocamento revela conteúdo ainda por criar e
 * a grelha «pisca». Três cobre um deslocamento de roda de rato normal sem
 * multiplicar o trabalho.
 */
export const MARGEM = 3;

export interface Janela {
  readonly viatura0: number;
  /** Exclusivo. */
  readonly viatura1: number;
  readonly dia0: number;
  /** Exclusivo. */
  readonly dia1: number;
}

// ============================================================================
// MEDIDAS POR ÍNDICE — cada coluna e cada linha com o seu tamanho
// ============================================================================
//
// ⚠️ ESTA É A ALTERAÇÃO ESTRUTURAL DO MAPA, e vale saber porque foi feita.
//
// A primeira versão redimensionava **todas** as colunas ao mesmo tempo, e a
// geometria era uma multiplicação: `left = dia × largura`. Simples e barata.
//
// O cliente experimentou e recusou: *«não gostei da forma como se arrastam as
// colunas e linhas, não funciona bem assim; tem de ser a mesma dinâmica do Excel:
// ao arrastar uma coluna ou linha só interfere no tamanho dessa linha, nunca em
// todas as outras»*. É o que uma folha de cálculo faz, e é o que os planeadores
// conhecem — a estrutura que eles conhecem é requisito (§ liberdade de desenho).
//
// Com tamanhos por índice a multiplicação deixa de servir. Passa a haver um vector
// de **posições acumuladas**: `offsets[i]` é onde o índice `i` começa, e
// `offsets[n]` é o total. Descobrir que índice está numa posição passa a ser uma
// **busca binária** em vez de uma divisão.
//
// O custo é real e está declarado: o vector reconstrói-se a cada alteração de
// medida (365 ou 250 somas — nada), mas a sua **identidade muda**, logo as linhas
// visíveis voltam a ser desenhadas. Por isso o arrasto é limitado a um quadro de
// animação: sem isso seria um redesenho por píxel de ponteiro.
// ============================================================================

/**
 * Posições acumuladas de `n` índices de tamanho `base`, com excepções por índice.
 *
 * Devolve `n + 1` valores: o último é o total. As excepções são esparsas — só as
 * colunas ou linhas que o utilizador arrastou.
 */
export function acumular(
  n: number,
  base: number,
  excepcoes?: ReadonlyMap<number, number>,
): number[] {
  const saida = new Array<number>(n + 1);
  saida[0] = 0;
  for (let i = 0; i < n; i++) {
    saida[i + 1] = (saida[i] ?? 0) + (excepcoes?.get(i) ?? base);
  }
  return saida;
}

/** Tamanho do índice `i`, lido do vector acumulado. */
export function tamanho(offsets: readonly number[], i: number): number {
  return (offsets[i + 1] ?? 0) - (offsets[i] ?? 0);
}

/**
 * Índice que contém a posição `pos` — busca binária.
 *
 * ⚠️ Substitui uma divisão, e é a única parte desta mudança que erra em silêncio:
 * um índice ao lado desenha o dia errado e a grelha continua com bom aspecto. Há
 * teste que a compara com a divisão em toda a gama, no caso uniforme.
 */
export function indiceEm(offsets: readonly number[], pos: number): number {
  let baixo = 0;
  let alto = offsets.length - 2; // o último índice válido, não o total
  if (alto < 0) return 0;
  while (baixo < alto) {
    const meio = (baixo + alto + 1) >> 1;
    if ((offsets[meio] ?? 0) <= pos) baixo = meio;
    else alto = meio - 1;
  }
  return baixo;
}

/**
 * Que parte da grelha desenhar.
 *
 * Os limites são fechados à esquerda e abertos à direita, como no resto do código.
 * A margem aplica-se dos dois lados e é recortada aos extremos.
 */
export function calcularJanela(
  deslocamento: { readonly topo: number; readonly esquerda: number },
  visivel: { readonly altura: number; readonly largura: number },
  total: { readonly viaturas: number; readonly dias: number },
  // ⚠️ As três medidas são AGORA VARIÁVEIS — o utilizador arrasta-as, como no
  // Excel. Passam por aqui em vez de serem lidas das constantes porque esta função
  // é a única aritmética do mapa que erra em silêncio: com a medida errada, a
  // janela fica desalinhada do que se vê e desenha-se o dia errado sem que nada
  // pareça mal.
  medidas: {
    readonly larguraViatura?: number;
    /** Posições acumuladas dos dias. Omitido = todos com `LARGURA_DIA`. */
    readonly dias?: readonly number[];
    /** Posições acumuladas das linhas. Omitido = todas com `ALTURA_LINHA`. */
    readonly linhas?: readonly number[];
  } = {},
): Janela {
  const larguraViatura = medidas.larguraViatura ?? LARGURA_VIATURA;
  const dias = medidas.dias ?? acumular(total.dias, LARGURA_DIA);
  const linhas = medidas.linhas ?? acumular(total.viaturas, ALTURA_LINHA);
  // ⚠️ O número de índices vem dos VECTORES e não de `total` quando eles existem.
  // Dois números para a mesma coisa acabam por discordar, e a geometria é a que
  // manda: é ela que decide onde as barras são desenhadas.
  const nLinhas = linhas.length - 1;
  const nColunas = dias.length - 1;

  // O cabeçalho ocupa espaço no conteúdo mas não mostra linhas; a coluna das
  // viaturas ocupa largura mas não mostra dias. Sem os descontar, a janela ficaria
  // adiantada em relação ao que se vê.
  const alturaUtil = Math.max(0, visivel.altura - ALTURA_CABECALHO);
  const larguraUtil = Math.max(0, visivel.largura - larguraViatura);

  // Recortar o deslocamento ao último ecrã possível.
  //
  // Sem isto, um deslocamento maior do que o conteúdo devolve `viatura0` acima de
  // `viatura1` — uma janela vazia, ou seja um mapa em branco. Acontece quando o
  // plano passa a ter menos viaturas com a grelha já deslocada, antes de o
  // navegador reajustar o `scrollTop`. Mostrar o último ecrã é o que o navegador
  // faz quando o conteúdo encolhe, e é o que o utilizador espera.
  const topo = Math.min(
    Math.max(0, deslocamento.topo),
    Math.max(0, (linhas[nLinhas] ?? 0) - alturaUtil),
  );
  const esquerda = Math.min(
    Math.max(0, deslocamento.esquerda),
    Math.max(0, (dias[nColunas] ?? 0) - larguraUtil),
  );

  // Quantas cabem: com tamanhos por índice não é uma divisão — percorre-se até
  // encobrir a área visível. São ~20 a ~60 iterações, e só quando a janela muda.
  const primeiraLinha = indiceEm(linhas, topo);
  let ultimaLinha = primeiraLinha;
  while (ultimaLinha < nLinhas && (linhas[ultimaLinha] ?? 0) < topo + alturaUtil) ultimaLinha++;
  const primeiraColuna = indiceEm(dias, esquerda);
  let ultimaColuna = primeiraColuna;
  while (ultimaColuna < nColunas && (dias[ultimaColuna] ?? 0) < esquerda + larguraUtil) {
    ultimaColuna++;
  }

  return {
    viatura0: Math.max(0, primeiraLinha - MARGEM),
    // `+ 1` mantém a linha parcialmente visível do lado de baixo, como a fórmula
    // anterior fazia com o `+ 1` depois do `ceil`. Sem ela, o último meio-item
    // aparecia só ao deslocar.
    viatura1: Math.min(nLinhas, ultimaLinha + 1 + MARGEM),
    dia0: Math.max(0, primeiraColuna - MARGEM),
    dia1: Math.min(nColunas, ultimaColuna + 1 + MARGEM),
  };
}

/** Duas janelas iguais não justificam voltar a desenhar. */
export function mesmaJanela(a: Janela, b: Janela): boolean {
  return (
    a.viatura0 === b.viatura0 &&
    a.viatura1 === b.viatura1 &&
    a.dia0 === b.dia0 &&
    a.dia1 === b.dia1
  );
}

/** Nome curto do mês, para a faixa superior do cabeçalho. */
export const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** Iniciais dos dias da semana, começando no domingo como `Date.getUTCDay()`. */
export const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;
